/**
 * Runtime configuration utility
 * 
 * This allows environment variables to be injected at container startup
 * instead of being embedded at build time. This is useful for Kubernetes/Rancher
 * deployments where you want to configure the app via environment variables.
 * 
 * Priority:
 * 1. window.__RUNTIME_CONFIG__ (injected at runtime)
 * 2. import.meta.env (build-time variables)
 */

// Extend Window interface to include runtime config
declare global {
  interface Window {
    __RUNTIME_CONFIG__?: {
      VITE_BASE_URL?: string
      VITE_PUBLIC_URL?: string
      VITE_ACCESS_TOKEN?: string
    }
  }
}

/**
 * Get an environment variable value
 * Checks runtime config first, then falls back to build-time env vars
 */
export function getEnvVar(key: 'VITE_BASE_URL' | 'VITE_PUBLIC_URL' | 'VITE_ACCESS_TOKEN'): string | undefined {
  // Check runtime config first (injected at container startup)
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) {
    const runtimeValue = window.__RUNTIME_CONFIG__[key]
    if (runtimeValue) {
      return runtimeValue
    }
  }
  
  // Fall back to build-time environment variables
  return import.meta.env[key] as string | undefined
}
